import { Link } from "react-router-dom";
import { Quote, ArrowRight, ShieldCheck, Star } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Overline } from "@/components/Reveal";
import { SITE, WHATSAPP_LINK } from "@/config/site";

const REVIEWS = [
    {
        name: "Anna Carla Angileri",
        when: "6 mesi fa",
        source: "Google",
        stars: 5,
        text: "Ho ricevuto un massaggio davvero eccellente, in un ambiente rilassante e curato nei minimi dettagli. La professionalità e la gentilezza di Sara mi hanno messo subito a mio agio. Ho sentito ogni tensione sciogliersi poco a poco, lasciandomi una profonda sensazione di benessere. Consiglio vivamente a chi cerca un momento di relax e cura di sé. Tornerò sicuramente!",
    },
    {
        name: "Laura Zanoli",
        when: "6 mesi fa",
        source: "Google",
        stars: 5,
        text: "Sono stata a fare dei massaggi, in particolare il californiano, e Sara ha delle mani d’oro. Davvero brava. Lo consiglio molto.",
    },
    {
        name: "Christelle Blanchet-Aïssaoui",
        when: "1 mese fa",
        source: "Google",
        stars: 5,
        text: "Oggi ho ricevuto un massaggio californiano davvero rilassante e rigenerante. Sara si è presa cura di me in modo impeccabile. Lo consiglio vivamente!",
    },
    {
        name: "Ale",
        when: "Messaggio diretto",
        source: "WhatsApp",
        stars: 0,
        text: "Ciao Sara, volevo ringraziarti per il meraviglioso trattamento di ieri. Sin da quando sono entrata nel tuo studio mi sono sentita accolta e coccolata! Il massaggio è stato super, sono uscita come nuova! Grazie per avermi fatto vivere un’ora di puro relax, in questo periodo ne ho proprio bisogno. Grazie per il tuo tocco delicato ma avvolgente e per la passione che trasmetti per il tuo lavoro. Tornerò presto!",
    },
];

const Stars = () => (
    <div className="flex gap-0.5">
        {[...Array(5)].map((_, i) => (
            <Star key={i} className="h-4 w-4 fill-sage text-sage" strokeWidth={1} />
        ))}
    </div>
);

const Recensioni = () => (
    <div data-testid="recensioni-page">
        <SEO
            title="Recensioni | Sara Solferini · Studio Olistico Bologna"
            description="Le recensioni autentiche dei clienti dello studio olistico a Bologna. Solo esperienze reali, pubblicate appena disponibili."
        />

        <header className="mx-auto max-w-6xl px-5 pb-16 pt-36 md:px-8 md:pt-44">
            <Reveal className="mx-auto max-w-2xl text-center">
                <Overline className="justify-center">Recensioni</Overline>
                <h1 className="mt-5 font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl" data-testid="recensioni-title">
                    Le parole di chi è <span className="italic text-sage">passato di qui</span>
                </h1>
                <div className="mt-8 inline-flex items-end gap-4" data-testid="google-rating-summary">
                    <span className="font-serif text-6xl font-light leading-none text-ink">4,8</span>
                    <div className="pb-1 text-left">
                        <Stars />
                        <p className="mt-1.5 text-xs text-fog">26 recensioni su Google</p>
                    </div>
                </div>
            </Reveal>
        </header>

        <section className="mx-auto max-w-6xl px-5 pb-28 md:px-8">
            <div className="grid gap-6 md:grid-cols-2" data-testid="reviews-grid">
                {REVIEWS.map((r, i) => (
                    <Reveal key={r.name} delay={i * 0.08}>
                        <figure
                            className="flex h-full flex-col rounded-3xl border border-border bg-card p-8 shadow-[0_4px_24px_rgba(67,46,30,0.05)]"
                            data-testid={`review-card-${i}`}
                        >
                            <div className="flex items-center justify-between">
                                {r.stars > 0 ? <Stars /> : <Quote className="h-6 w-6 text-beige" strokeWidth={1.5} />}
                                <span className="text-xs text-fog">{r.when}</span>
                            </div>
                            <blockquote className="mt-5 flex-1 font-serif text-lg font-light italic leading-relaxed text-clay">
                                “{r.text}”
                            </blockquote>
                            <figcaption className="mt-6 flex items-center justify-between border-t border-border/60 pt-4">
                                <span className="text-sm font-medium text-ink">{r.name}</span>
                                <span className="text-[11px] uppercase tracking-[0.2em] text-sage">{r.source}</span>
                            </figcaption>
                        </figure>
                    </Reveal>
                ))}
            </div>

            <Reveal>
                <p className="mt-8 flex items-center justify-center gap-2 text-center text-xs text-fog" data-testid="reviews-authenticity-note">
                    <ShieldCheck className="h-4 w-4 text-sage" strokeWidth={1.5} />
                    Recensioni reali raccolte su Google e dai messaggi dei clienti, pubblicate integralmente.
                </p>
            </Reveal>

            <Reveal delay={0.15}>
                <div className="mt-12 rounded-3xl bg-sand p-10 text-center md:p-12" data-testid="recensioni-invito">
                    <h3 className="font-serif text-2xl text-ink">Hai già ricevuto un trattamento?</h3>
                    <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-clay">
                        La tua esperienza può aiutare altre persone a scegliere con serenità. Scrivila via
                        email o WhatsApp: la pubblicheremo con il tuo consenso.
                    </p>
                    <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
                        <a
                            href={`mailto:${SITE.email}?subject=${encodeURIComponent("La mia esperienza allo studio")}`}
                            data-testid="recensioni-email-cta"
                            className="rounded-full border border-sage px-7 py-3 text-sm font-semibold uppercase tracking-widest text-sage transition-colors duration-300 hover:bg-sage hover:text-cream"
                        >
                            Scrivi una email
                        </a>
                        <a
                            href={WHATSAPP_LINK}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="recensioni-whatsapp-cta"
                            className="rounded-full bg-sage px-7 py-3 text-sm font-semibold uppercase tracking-widest text-cream transition-colors duration-300 hover:bg-sage-dark"
                        >
                            Scrivi su WhatsApp
                        </a>
                    </div>
                    <Link
                        to="/prenota"
                        data-testid="recensioni-prenota-link"
                        className="group mt-8 inline-flex items-center gap-2 text-sm font-medium uppercase tracking-widest text-sage"
                    >
                        Oppure prenota il tuo primo trattamento
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                    </Link>
                </div>
            </Reveal>
        </section>
    </div>
);

export default Recensioni;
